import { Button } from "@material-ui/core";
import ExpandMore from "@material-ui/icons/ExpandMore";
import flattenHierarchy from "backend/flattenHierarchy";
import useTopElements from "backend/hooks/model/useTopElements";
import { ICardElementWithChildren } from "backend/model/model";
import clsx from "clsx";
import { t } from "i18next";
import { makeThemedStyles } from "qpr-base/viewcomponents/utils";
import React from "react";
import DepartmentPopover from "../popups/DepartmentPopover";
import { useAppContext } from "qpr-base/context/AppCtxProvider";

const useDivisionCardStyles = makeThemedStyles("DivisionCard", (theme) => ({
  root: {
    width: "250px",
    borderRadius: "2px",
    color: "white",
  },
  card: {
    width: "100%",
    height: "130px",
    borderRadius: "1.6em",
    overflow: "hidden",
    position: "relative",
    background:
      "linear-gradient(270deg, rgba(16, 41, 117, 0.37) -0.1%, rgba(16, 41, 117, 0.83) 83.43%)",
  },
  content: {
    width: "100%",
    padding: "1.9em 2em 1.4em 2em",
    height: "calc(100% - 3.8em)",
    background:
      "linear-gradient(270deg, rgba(16, 41, 117, 0.17) -0.1%, rgba(16, 41, 117, 0.83) 83.43%)",
  },
  type: {
    color: "rgba(245, 246, 246, 1)",
    fontSize: "1.2em",
    lineHeight: "1.25",
    fontWeight: 500,
    marginBottom: "0.66em",
  },
  name: {
    color: "rgba(255, 255, 255, 1)",
    fontSize: "1.4em",
    lineHeight: "1.428",
    fontWeight: 600,
    letterSpacing: "0.05625px",
    maxWidth: "15.07em",
  },
  title: {
    color: "white",
  },
  titleTypeName: {
    color: "white",
  },
  cardActions: {
    background: "rgba(255, 255, 255, 1)",
    width: "100%",
    height: "3.8em",
  },
  cardActionsButton: {
    background: "rgba(255, 255, 255, 1)",
    width: "100%",
    height: "100%",
    color: "rgba(16, 41, 117, 1)",
    fontSize: "1.2em",
    borderRadius: 0,
    display: "flex",
    justifyContent: "space-between",
    padding: "0 1.5833em",
    "&.RTL": {
      "& svg": {
        transform: "rotate(90deg)",
      },
    },
    "& svg": {
      fill: "rgba(63, 69, 79, 1)",
      transform: "rotate(-90deg)",
    },
  },
  disabledCardActionsButton: {
    background: "rgba(255, 255, 255, 1)",
    width: "100%",
    height: "100%",
    color: "rgb(140, 142, 148)",
    fontSize: "1.2em",
    fontStyle: "Italic",
    borderRadius: 0,
    display: "flex",
    justifyContent: "space-between",
    padding: "0 1.5833em",
    "&.RTL": {
      "& svg": {
        transform: "rotate(90deg)",
      },
    },
    "& svg": {
      fill: "rgba(63, 69, 79, 1)",
      transform: "rotate(-90deg)",
    },
  },
  range: {
    position: "absolute",
    top: "1.2em",
    height: "1.8em",
    width: "1.8em",
    borderRadius: "50%",
    background: "rgba(221, 81, 70, 1)",
    border: "2px solid rgb(255, 255, 255)",
  },
}));

export const DivisionCardTitle = (props: React.PropsWithChildren<{}>) => {
  const styles = useDivisionCardStyles();
  return <div className={styles.title}>{props.children}</div>;
};

export const DivisionCardTypeName = (props: React.PropsWithChildren<{}>) => {
  const styles = useDivisionCardStyles();
  return <div className={styles.titleTypeName}>{props.children}</div>;
};

export const DivisionCard = (
  props: React.PropsWithChildren<{
    scorecard: ICardElementWithChildren;
    translatedName: string;
    topElement: ICardElementWithChildren;
    cardTitle?: string;
    rangeName?: string;
    rangeColor?: string;
    backgroundImage?: string;
    departmentIcon?: string;
    rtl?: boolean;
    onSelect?: () => void;
    onSelectChild?: (item: ICardElementWithChildren) => void;
    typenames: {
      name: string;
      title: string;
    }[];
  }>
) => {
  const ctx = useAppContext();
  const ref = React.useRef<HTMLButtonElement>(null);
  const [showPopover, setShowPopover] = React.useState(false);
  const styles = useDivisionCardStyles();
  const { data: topElements, isLoading: topElementsLoading } = useTopElements();
  const { scorecard } = props;
  const childrenDirect = React.useMemo(
    () => scorecard.children,
    [props.scorecard]
  );
  const childrenFlat = React.useMemo(
    () => flattenHierarchy(scorecard.children),
    [props.scorecard]
  );
  const divisionCount = childrenDirect.length;
  const accessibleSubItemCount =
    childrenDirect.filter((f) => f?.name !== undefined).length +
    childrenDirect.reduce(
      (prev, curr) =>
        prev + curr.children.filter((f) => f?.name !== undefined).length,
      0
    );
  const departmentCount = childrenFlat.length - divisionCount;
  const typeCounts = React.useMemo(() => {
    return props.typenames.map((tn) => {
      return {
        typename: tn.name,
        title: tn.title,
        count: childrenFlat.filter((el) => el.typename === tn.name).length,
      };
    });
  }, [childrenFlat]);

  const hasTotalCountGreaterThanZero = React.useMemo(() => {
    return typeCounts.reduce((prev, curr) => prev + curr.count, 0) > 0;
  }, []);

  if (!topElements || topElementsLoading) return null;

  const hasRights = () => scorecard?.name !== undefined;

  return (
    <div
      className={styles.root}
      role="button"
      style={{
        cursor: props.onSelect && hasRights() ? "Pointer" : undefined,
      }}
      tabIndex={0}
      onKeyDown={() => {}}
      onClick={() => {
        if (props.onSelect && !showPopover) {
          props.onSelect();
        }
      }}
    >
      <div className={styles.card}>
        <div
          className={styles.range}
          title={
            t("configtypes." + props.rangeName) ||
            t("configtypes." + scorecard.rangename) ||
            t("configtypes.No value")
          }
          style={{
            background: `#${
              props.rangeColor || props.topElement.rangecolor || "000000"
            }aa`,
            right: !ctx.rtl ? "1.2em" : "auto",
            left: ctx.rtl ? "1.2em" : "auto",
          }}
        ></div>
        <div
          className={styles.content}
          style={{
            background: `linear-gradient(270deg, rgba(16, 41, 117, 0.17) -0.1%, rgba(16, 41, 117, 0.83) 83.43%), url(${props.backgroundImage})`,
          }}
        >
          <div className={styles.type}>
            {props.cardTitle || t("configtypes." + scorecard.typename)}
          </div>
          <div className={styles.name}>{props.translatedName}</div>
        </div>

        <div className={styles.cardActions}>
          <Button
            onClick={(e) => {
              if (hasTotalCountGreaterThanZero) {
                setShowPopover(true);
              }
              e.preventDefault();
              e.stopPropagation();
            }}
            className={clsx(
              accessibleSubItemCount > 0
                ? styles.cardActionsButton
                : styles.disabledCardActionsButton,
              props.rtl && "RTL"
            )}
            endIcon={hasTotalCountGreaterThanZero ? <ExpandMore /> : null}
            ref={ref}
          >
            <React.Fragment>
              {divisionCount > 0 && (
                <>
                  {divisionCount}{" "}
                  {divisionCount === 1
                    ? t("cascade.Division")
                    : t("cascade.Divisions")}{" "}
                </>
              )}
              {departmentCount > 0 && (
                <>
                  {departmentCount > 0
                    ? ` and ${departmentCount} ${
                        departmentCount === 1
                          ? t("cascade.Department")
                          : t("cascade.Departments")
                      }`
                    : ""}
                </>
              )}
            </React.Fragment>
          </Button>

          <DepartmentPopover
            onClose={() => {
              setShowPopover(false);
            }}
            onSelect={props.onSelect}
            onSelectChild={props.onSelectChild}
            departmentIcon={props.departmentIcon}
            items={childrenDirect}            
            anchor={ref.current}
            typenames={["Scorecard"]}
            isOpen={showPopover}
          />
        </div>
      </div>
    </div>
  );
};
